import { Info } from 'lucide-react';
import * as React from 'react';

// =============================================================================
// cgu-resignature-banner.tsx — Bandeau contextuel de RESIGNATURE des CGU (DBU §3.1).
// =============================================================================
// DBU §3.1 « Écran de signature » : « Bandeau contextuel en haut de l'écran lors d'une resignature,
// précisant la date de révision et proposant un lien vers le résumé des évolutions (changelog) fourni
// par l'admin lors de la publication. » PRÉSENTATIONNEL : la décision « resignature ou onboarding »
// (statut de signature, autorité API via GET has-signed) est passée par l'appelant (cgu-screen).
//
// RENDU DU RÉSUMÉ DES ÉVOLUTIONS (LANE-D, DBU §3.1) : le changelog est désormais exposé par le contrat
// FOURNISSEUR (GET /legal/cgu/current/changelog → CguChangelogView { cguVersionId, versionNumber,
// changelog }). Le résumé est fourni par l'admin à la publication et servi en TEXTE BRUT (décision
// GATE_HUMAN). Ici il est rendu en NŒUD TEXTE React (échappement natif ; JAMAIS dangerouslySetInnerHTML,
// aucun sanitiseur requis). Optionnel / vide ⇒ disclosure non affichée (aucun lien mort).
//
// WCAG 2.1 AA : role="status" (annonce non bloquante), icône décorative aria-hidden, message texte
// explicite (jamais couleur seule) ; disclosure native <details>/<summary> (activable clavier).

export interface CguResignatureBannerProps {
  /** Numéro de version cible à signer (format `CGU-AAAA-MM`). */
  readonly versionNumber: string;
  /**
   * Résumé des évolutions (changelog) fourni par l'admin à la publication (DBU §3.1). TEXTE BRUT :
   * rendu en nœud texte React (échappement natif ; jamais dangerouslySetInnerHTML). Optionnel /
   * chaîne vide ⇒ disclosure non affichée (aucun lien mort). `| undefined` explicite : le call site
   * passe `changelog.data?.changelog` (undefined tant que non chargé), sous exactOptionalPropertyTypes.
   */
  readonly changelog?: string | undefined;
}

export function CguResignatureBanner({
  versionNumber,
  changelog,
}: CguResignatureBannerProps): React.JSX.Element {
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-md border border-accent/50 bg-accent/10 p-4"
    >
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">
          Actualisation des conditions générales
        </p>
        <p className="text-sm text-foreground">
          Vous vous apprêtez à signer la version {versionNumber}, qui remplace la version que vous
          aviez précédemment acceptée.
        </p>
        {changelog !== undefined && changelog.trim().length > 0 ? (
          <details className="text-sm text-foreground">
            <summary className="cursor-pointer font-medium underline underline-offset-2">
              Consulter le résumé des évolutions
            </summary>
            {/* TEXTE BRUT rendu en nœud texte React : échappement natif (aucun HTML injecté). */}
            <p className="mt-2 whitespace-pre-line">{changelog}</p>
          </details>
        ) : null}
      </div>
    </div>
  );
}
